import { FC, useState, useRef, useEffect } from 'react';
import cx from 'classnames';

import useOnScreen from 'hooks/useOnScreen';
import useReducedMotion from 'hooks/useReducedMotion';

import ConditionalWrapper from 'atoms/ConditionalWrapper';

import { VideoField as VideoFieldType } from 'lib/sanity/types';

export interface Props {
  media: VideoFieldType;
  isHorizontal?: boolean;
  showPlayPause?: boolean;
  isGridViewVideoPaused?: boolean;
}

const VideoBlock: FC<Props> = ({
  media,
  isHorizontal,
  showPlayPause,
  isGridViewVideoPaused,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const isReducedMotion = useReducedMotion();
  const videoSrc = media.src;
  const isTextural = media.videoType === 'textural';

  const onScreen = isTextural ? useOnScreen(ref) : useOnScreen(videoRef);

  const handlePlayVideo = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsVideoPaused(false);
      } else {
        videoRef.current.pause();
        setIsVideoPaused(true);
      }
    }
  };

  useEffect(() => {
    setAutoPlay(!isReducedMotion && isTextural);

    if (isGridViewVideoPaused) {
      if (videoRef.current) {
        videoRef.current.pause();
        setIsVideoPaused(true);
      }
    } else {
      if (videoRef.current) {
        videoRef.current.play();
        setIsVideoPaused(false);
      }
    }
  }, [isReducedMotion, isTextural, isGridViewVideoPaused]);

  if (!videoSrc) {
    return <></>;
  }

  return (
    <>
      {showPlayPause ? (
        <video
          autoPlay={autoPlay}
          muted={isTextural}
          loop
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover"
          ref={videoRef}
          controls={!isTextural}
          src={videoSrc}
        />
      ) : (
        <ConditionalWrapper
          condition={isTextural}
          wrapper={(children) => (
            <div
              ref={ref}
              className={cx(
                'Video relative w-full flex justify-center items-center',
                {
                  'Video__cursor--play': isVideoPaused,
                  'Video__cursor--pause': !isVideoPaused,
                },
              )}
            >
              <div
                className={cx(
                  'absolute w-full h-full bg-black transition-opacity pointer-events-none',
                  {
                    'opacity-40': isVideoPaused,
                    'opacity-0': !isVideoPaused,
                  },
                )}
              ></div>
              <div
                className={cx(
                  'Video__button-container absolute flex px-3 py-3 w-full h-full pointer-events-none',
                )}
              >
                <button
                  className={cx(
                    'Video__play-button absolute tablet:text-desktop-med text-small font-light text-center w-[80px] h-[40px] tablet:w-[120px] tablet:h-[60px] flex justify-center items-center bg-yellow rounded-full hover:cursor-pointer z-10',
                  )}
                  onClick={() => handlePlayVideo()}
                >
                  {isVideoPaused ? 'Play' : 'Pause'}
                </button>
              </div>
              {children}
            </div>
          )}
        >
          <video
            autoPlay={autoPlay}
            muted={isTextural}
            loop
            playsInline
            className={cx('Video__element w-full', {
              'Video__element--horizontal': isHorizontal,
              'Video__element--vertical': !isHorizontal,
            })}
            ref={videoRef}
            onClick={isTextural ? () => handlePlayVideo() : undefined}
            controls={!isTextural}
          >
            {onScreen && <source src={videoSrc} type="video/mp4" />}
          </video>
        </ConditionalWrapper>
      )}
    </>
  );
};

export default VideoBlock;
